import { useCallback, useState } from 'react';
import DrawerFlow, { DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import { TemplateCreationData } from 'src/company/templates/types';
import { initialTemplateCreationData, TemplateCreationProvider } from './TemplateCreationProvider';
import SelectTemplateTypeStep from './steps/SelectTemplateTypeStep';
import UploadDocumentsStep from './steps/UploadDocumentsStep';
import EditSectionContentStep from './steps/EditSectionContentStep';
import EditSectionInstructionsStep from './steps/EditSectionInstructionsStep';
import FinalizeTemplateStep from './steps/FinalizeTemplateStep';
import ImportMethodStep from './steps/ImportMethodStep';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import SelectCompanyStep from 'src/company/templates/components/template-creation-flow/steps/SelectCompanyStep';
import {
  TEMPLATE_CREATION_FLOW_SCREEN_IDS,
  TEMPLATE_CREATION_FLOW_STEP_IDS,
} from './templateCreationFlowIds';

type TemplateCreationFlowProps = {
  opened: boolean;
  close: () => void;
  company: Company;
};

const steps: DrawerFlowStep<TemplateCreationData>[] = [
  {
    title: 'Select company',
    screens: [
      {
        component: SelectCompanyStep,
        maxContainerWidth: 'lg',
      },
    ],
  },
  {
    title: 'Select template type',
    screens: [
      {
        component: SelectTemplateTypeStep,
        maxContainerWidth: 'lg',
      },
    ],
  },
  {
    title: 'Template details',
    screens: [
      {
        component: FinalizeTemplateStep,
        allowVerticalOverflow: true,
      },
    ],
  },
  {
    title: 'Import method',
    screens: [
      {
        component: ImportMethodStep,
        allowVerticalOverflow: true,
        maxContainerWidth: 'lg',
      },
    ],
  },
  {
    title: 'Upload documents',
    screens: [
      {
        component: UploadDocumentsStep,
        allowVerticalOverflow: true,
        maxContainerWidth: 'lg',
      },
    ],
  },
  {
    title: 'Edit sections',
    id: TEMPLATE_CREATION_FLOW_STEP_IDS.editSections,
    screens: [
      {
        component: EditSectionContentStep,
        allowVerticalOverflow: true,
        id: TEMPLATE_CREATION_FLOW_SCREEN_IDS.editSectionContent,
      },
      {
        component: EditSectionInstructionsStep,
        allowVerticalOverflow: true,
        id: TEMPLATE_CREATION_FLOW_SCREEN_IDS.editSectionInstructions,
      },
    ],
  },
];

export default function TemplateCreationFlow({
  opened,
  close,
  company,
}: TemplateCreationFlowProps) {
  const [data, setData] = useState<TemplateCreationData>({
    ...initialTemplateCreationData,
    company,
  });

  const onSuccess = useCallback(() => {
    close();
    // TODO: Show success notification or refresh templates list
  }, [close]);

  return (
    <TemplateCreationProvider company={company}>
      <DrawerFlow
        opened={opened}
        close={close}
        title="Create template"
        steps={steps}
        initialData={{ ...initialTemplateCreationData, company }}
        data={data}
        setData={setData}
        onSuccess={onSuccess}
        drawerProps={{ closeOnEscape: false }}
      />
    </TemplateCreationProvider>
  );
}
